import { ErrorHandler, Injectable, Injector, OnDestroy } from '@angular/core';
import { PlatformCommand } from '@shared/commands/command-base';
import { CommandService } from './command.service';
import { ObservationContextService } from '../../copilot/services/observation-context.service';

/** One thing the browser could not cope with, as the trail records it. */
export interface CapturedFrontendError {
  /** The error's message — the one line a reader scans. */
  message: string;
  /** Its stack as the browser rendered it, clipped before it is sent. */
  stack: string;
  /** The url the user was on when it threw. WHICH SCREEN broke. */
  url: string;
  /** When it threw, by this browser's clock — the one the user was actually on. */
  at: string;
  /** Whether it arrived as an unhandled promise rejection rather than a thrown error. */
  isRejection: boolean;
}

/**
 * WHAT BREAKS IN FRONT OF THE PERSON, PUT IN THE RUN'S OWN TRAIL (MISN-E-71-06, ADR 0070).
 *
 * This is the one part of an observed execution the server cannot see for itself. When Mission navigates
 * a user to a screen and asks them to finish something by hand, and that screen throws, the API's records
 * show a navigation issued and then nothing at all — which is exactly what they show when the person
 * simply walked away. Only this browser knows the difference, so only this browser can report it.
 *
 * IT CAPTURES ONLY WHILE AN OBSERVATION IS CURRENT, and that is a hard rule rather than a filter applied
 * later. Every ordinary page in the platform throws something eventually and none of it belongs to
 * anybody's run: a handler that sent unconditionally would turn the trail store into an error sink and
 * age out the runs somebody IS watching to make room for the ones nobody is. Outside an Observation
 * nothing is captured, nothing is buffered, and no command is composed.
 *
 * IT BATCHES, at most twenty errors or five seconds. The failure this exists to record is very often a
 * widget throwing inside a render loop, and one request per error would answer a broken screen with a
 * request storm. The batch is transport only — the server files one entry per error, at the moment each
 * one happened.
 *
 * THE DEFAULT HANDLER RUNS FIRST, ALWAYS. Everything an Angular developer relies on today — the error in
 * the console, with its stack, at the moment it happened — is untouched, and it happens before any of
 * this: a handler that swallowed errors while trying to record them would trade a debuggable app for a
 * readable trail.
 */
@Injectable()
export class ObservationErrorHandler extends ErrorHandler implements OnDestroy {
  /** The command the batch is reported as. Composed here; the server's own constant is its twin. */
  static readonly CommandName = 'RecordFrontendErrorsCommand';
  static readonly CommandApi = 'XatBotApi';

  /** Twenty errors, and the batch goes now rather than at the next tick. */
  static readonly MaxBatch = 20;

  /**
   * Five seconds. A PUBLIC STATIC rather than a private constant for the reason the window service's
   * idleTimeoutMs is public: the claim worth testing is "errors arrive as one batch", not that a fixture
   * can stand still for five seconds to watch it.
   */
  static readonly FlushIntervalMs = 5000;

  /**
   * The stack is clipped HERE, not only at the server's payload cap. A `Maximum call stack size exceeded`
   * carries tens of thousands of frames, and the request that reports it goes out from a screen that is
   * already struggling.
   */
  static readonly MaxStackChars = 4000;
  static readonly MaxMessageChars = 1000;

  private batch: CapturedFrontendError[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Set for the whole of a flush. Composing and posting a command can itself throw, and that throw comes
   * straight back here as a handled error — without this the first failed report becomes an unbounded
   * recursion on a page that is already broken.
   */
  private flushing = false;

  private readonly onUnhandledRejection = (event: PromiseRejectionEvent): void => {
    this.capture(event?.reason, true);
  };

  /**
   * The Injector rather than the services themselves. An ErrorHandler is constructed during bootstrap,
   * before most of the app's providers exist, and constructor-injecting CommandService here would both
   * force it up early and put this class inside its dependency graph — the same lazy resolve, and the
   * same reason, as CommandService's own use of PlatformToolLayoutService.
   */
  constructor(private injector: Injector) {
    super();

    // Rejections never reach an Angular ErrorHandler: a promise nobody caught is a browser-level event,
    // and in an app whose data access is all HTTP promises and resolvers it is the MORE common of the
    // two failures. Registered once, for the life of the app.
    window.addEventListener('unhandledrejection', this.onUnhandledRejection);
  }

  /**
   * THE DEFAULT BEHAVIOUR FIRST — the console error every developer already relies on — and only then
   * the trail. In this order because the capture is the optional half: if composing a command ever
   * throws, the developer has already seen the real error.
   */
  override handleError(error: any): void {
    super.handleError(error);
    this.capture(error, false);
  }

  /** Sends anything still buffered. Called by the timer, by a full batch, and by the tests. */
  flush(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.batch.length === 0) {
      return;
    }

    const observationId = this.currentObservationId();
    const errors = this.batch;
    this.batch = [];

    // The Observation ended between the capture and the flush — the window closed, the run finished.
    // Dropped rather than sent unattached: an entry with no trail has nowhere to be filed.
    if (!observationId) {
      return;
    }

    this.flushing = true;

    try {
      const command = new PlatformCommand(
        ObservationErrorHandler.CommandName, ObservationErrorHandler.CommandApi);
      command.projectId = '[PROJECT_ID]';
      command.waitForResponse = false;
      // NOT THE PERSON ACTING. The capture hook on the server files every command that runs under the
      // current Observation, and tells the runtime's from the USER's by IsUserAction — stamped true,
      // which is what CommandService does to everything it is not told otherwise about, this report
      // would show as a click nobody made, inside the very window whose purpose is saying which half
      // the person did. It is also what keeps the report out of the platform usage report.
      command.isUserAction = false;

      command.fieldValues['ObservationId'] = observationId;
      // A JSON STRING, not an array: PlatformCommand.GetValue<T> on the server is a hard cast, and an
      // array arrives there as a JToken over HTTP but as a List<T> when a server caller builds the same
      // command in process. One string reads the same however it travelled — the rule the window
      // slice's AllowedRoutes established.
      command.fieldValues['Errors'] = JSON.stringify(errors.map(error => ({
        Message: error.message,
        Stack: error.stack,
        Url: error.url,
        At: error.at,
        IsRejection: error.isRejection
      })));
      command.fieldValues['Count'] = errors.length;
      command.fieldValues['At'] = new Date().toISOString();

      this.injector.get(CommandService).executePlatformCommand(command);
    } catch {
      // A report that cannot be sent is not worth a second error on a page that is already failing, and
      // there is nowhere to put one: the caller is a timer or an error handler.
    } finally {
      this.flushing = false;
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('unhandledrejection', this.onUnhandledRejection);

    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /** Buffer one error, if this browser is inside an observed execution. Otherwise do nothing at all. */
  private capture(error: any, isRejection: boolean): void {
    if (this.flushing) {
      return;
    }

    const observationId = this.currentObservationId();
    if (!observationId) {
      return;
    }

    this.batch.push({
      message: this.clip(this.messageOf(error), ObservationErrorHandler.MaxMessageChars),
      stack: this.clip(this.stackOf(error), ObservationErrorHandler.MaxStackChars),
      url: window.location?.href ?? '',
      at: new Date().toISOString(),
      isRejection
    });

    if (this.batch.length >= ObservationErrorHandler.MaxBatch) {
      this.flush();
      return;
    }

    if (this.flushTimer === null) {
      this.flushTimer = setTimeout(() => this.flush(), ObservationErrorHandler.FlushIntervalMs);
    }
  }

  /**
   * The trail, or null. Resolved lazily and defensively: this runs on the error path, and an injector
   * that cannot produce the context service is a reason to record nothing, never a reason to throw from
   * inside the error handler.
   */
  private currentObservationId(): string | null {
    try {
      return this.injector.get(ObservationContextService).current;
    } catch {
      return null;
    }
  }

  /** Whatever the thing that was thrown is willing to say it was. Never a serialized error object. */
  private messageOf(error: any): string {
    if (error == null) { return 'Unknown error'; }
    if (typeof error === 'string') { return error; }

    // An Angular HttpErrorResponse and a rejected fetch both wrap the real thing one level down, and the
    // outer message ("Http failure response for /api/x") is the less useful of the two.
    const message = error.message ?? error.rejection?.message ?? error.error?.message;

    return typeof message === 'string' && message.length > 0 ? message : String(error);
  }

  private stackOf(error: any): string {
    const stack = error?.stack ?? error?.rejection?.stack ?? error?.error?.stack;
    return typeof stack === 'string' ? stack : '';
  }

  private clip(value: string, max: number): string {
    if (!value) { return ''; }
    return value.length <= max ? value : `${value.substring(0, max)}…[truncated]`;
  }
}
